if (!customElements.get('custom-popup')) {
  customElements.define(
    'custom-popup',
    class CustomPopup extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog) return;

        this.sectionId = this.dataset.sectionId;
        this.storageKey = `custom-popup:${this.sectionId}`;

        this.querySelectorAll('[data-popup-close]').forEach((button) =>
          button.addEventListener('click', () => this.close())
        );

        // Mark as seen before the link is followed: a same-tab navigation would
        // otherwise unload the page before the dialog's async 'close' event fires.
        this.querySelectorAll('[data-popup-cta]').forEach((link) =>
          link.addEventListener('click', () => this.close())
        );

        // Clicks on the backdrop report the dialog itself as the target, because
        // .custom-popup__inner covers the whole dialog box.
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });

        // Esc closes the dialog natively; route it through close() so it's recorded.
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.close();
        });

        if (window.Shopify && Shopify.designMode) {
          this.bindThemeEditor();
          return;
        }

        if (this.hasBeenSeen()) return;

        const delay = parseInt(this.dataset.delay, 10) || 0;
        this.openTimer = setTimeout(() => this.open(), delay * 1000);
      }

      disconnectedCallback() {
        clearTimeout(this.openTimer);
        document.body.classList.remove('overflow-hidden');

        if (this.editorHandlers) {
          Object.entries(this.editorHandlers).forEach(([name, handler]) =>
            document.removeEventListener(name, handler)
          );
        }
      }

      open() {
        if (this.dialog.open) return;
        this.dialog.showModal();
        document.body.classList.add('overflow-hidden');
      }

      close() {
        if (!(window.Shopify && Shopify.designMode)) this.markSeen();
        if (!this.dialog.open) return;
        this.dialog.close();
        document.body.classList.remove('overflow-hidden');
      }

      // In the theme editor, ignore frequency/storage and show the popup only
      // while its section is selected, so it never blocks editing other sections.
      bindThemeEditor() {
        const isThisSection = (event) => event.detail && event.detail.sectionId === this.sectionId;

        this.editorHandlers = {
          'shopify:section:select': (event) => isThisSection(event) && this.open(),
          'shopify:section:deselect': (event) => isThisSection(event) && this.close(),
          // A settings change re-renders the section while it is still selected
          // and fires 'load' (not 'select') for the new copy, so reopen on load.
          'shopify:section:load': (event) => isThisSection(event) && this.open(),
        };

        Object.entries(this.editorHandlers).forEach(([name, handler]) =>
          document.addEventListener(name, handler)
        );
      }

      hasBeenSeen() {
        try {
          switch (this.dataset.frequency) {
            case 'session':
              return sessionStorage.getItem(this.storageKey) !== null;
            case '12h': {
              const seenAt = parseInt(localStorage.getItem(this.storageKey), 10);
              return !isNaN(seenAt) && Date.now() - seenAt < 12 * 3600000;
            }
            case 'days': {
              const seenAt = parseInt(localStorage.getItem(this.storageKey), 10);
              const days = parseInt(this.dataset.days, 10) || 1;
              return !isNaN(seenAt) && Date.now() - seenAt < days * 86400000;
            }
            default:
              return false;
          }
        } catch (error) {
          // Storage can be blocked (private mode, cookie settings); just show the popup.
          return false;
        }
      }

      markSeen() {
        try {
          if (this.dataset.frequency === 'session') sessionStorage.setItem(this.storageKey, '1');
          // Both timed options store when the popup was dismissed; hasBeenSeen() compares against it.
          if (this.dataset.frequency === '12h' || this.dataset.frequency === 'days') {
            localStorage.setItem(this.storageKey, String(Date.now()));
          }
        } catch (error) {
          // Ignore storage failures; the popup will simply show again.
        }
      }
    }
  );
}
